import { ChangeDetectionStrategy, Component, OnInit, Signal, ViewChild, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';

import {
    BrandConfigService,
    Links,
    OivCoreSharedFacade,
    RouteHelper,
    TealiumTrackingFacade,
    WINDOW,
} from '@mobi/oiv-viewer-utils-ng-jslib';
import { MobiUiLanguage } from '@mobi/rwc-ui-components-ng-jslib';

import * as de from './app.translation.de.json';
import * as fr from './app.translation.fr.json';
import * as it from './app.translation.it.json';
import { FooterComponent } from './layout/ui/footer/footer.component';
import { HeaderComponent, HeaderIcon } from './layout/ui/header/header.component';

@Component({
    imports: [RouterModule, FooterComponent, HeaderComponent],
    selector: 'oiv-viewer-b2c-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
    @ViewChild('header', { static: true }) headerComponent!: HeaderComponent;
    #title = inject(Title);
    #oivCoreSharedFacade = inject(OivCoreSharedFacade);
    #tealiumTrackingFacade = inject(TealiumTrackingFacade);
    #routeHelper = inject(RouteHelper);
    #router = inject(Router);
    #window = inject(WINDOW);
    #brandConfigService = inject(BrandConfigService);

    links: Signal<Links> = this.#oivCoreSharedFacade.links;

    #appTitle = toSignal(this.#oivCoreSharedFacade.translatedMessage('oiv.app.title'), {
        initialValue: 'Allgemeine Bedingungen',
    });

    /**
     * Brand name - dynamically loaded based on detected brand
     * For Mobiliar: 'oiv.app.mobiliar' → 'die Mobiliar'
     * For Protekta: 'oiv.app.protekta' → 'Protekta'
     */
    #appBrandName = toSignal(
        this.#oivCoreSharedFacade.translatedMessage(this.#brandConfigService.getBrand().translationBrandKey),
        {
            initialValue: this.#brandConfigService.isProtekta() ? 'Protekta' : 'die Mobiliar',
        },
    );

    isDisplayIcon = computed((): boolean => {
        // Display the menu icon only on the AVB page and on mobile
        return (
            this.#routeHelper.isAvbPage(this.#oivCoreSharedFacade.currentUrl()) && this.#oivCoreSharedFacade.isMobile()
        );
    });

    constructor() {
        this.#oivCoreSharedFacade.translations = { de: de, fr: fr, it: it };

        // Update page title when translations change
        effect(() => {
            this.#title.setTitle(`${this.#appTitle()} | ${this.#appBrandName()}`);
        });

        this.#triggerClosedOverlayEffect();
    }

    ngOnInit() {
        this.#oivCoreSharedFacade.setScreenMode();
    }

    backToHomePage() {
        const homePageUrl = this.#routeHelper.getHomePageURL(
            this.#router,
            this.#oivCoreSharedFacade.appContext(),
            this.#oivCoreSharedFacade.isApprovalMode(),
        );
        this.#tealiumTrackingFacade.trackButtonClickEvent('header-logo-click', homePageUrl);
        this.#router.navigate([homePageUrl]);
        this.#oivCoreSharedFacade.closeOverlay();
    }

    onHeaderIconClick(headerIcon: HeaderIcon) {
        const icon = headerIcon.icon;
        if (icon === 'menu') {
            this.#tealiumTrackingFacade.trackButtonClickEvent('menu', '');
            this.#oivCoreSharedFacade.openOverlay();
        } else {
            this.#oivCoreSharedFacade.closeOverlay();
        }
    }

    #triggerClosedOverlayEffect() {
        // Close the x icon and replace by menu icon when the overlay is closed
        effect(() => {
            if (!this.#oivCoreSharedFacade.isOverlayOpen()) {
                this.headerComponent.enableMenuIcon();
            }
        });
    }

    updateLanguage(language: MobiUiLanguage) {
        const route = this.#routeHelper.getRoutePathOnProdStage(language);
        if (route) {
            this.#window.location.href = `https://${route}`;
        } else {
            if (this.#brandConfigService.isProtekta()) {
                this.#router.navigateByUrl(`/${language}${this.#routeHelper.removeFirstPath(this.#router.url)}`);
            }
            this.#oivCoreSharedFacade.changeLanguage(language);
        }
        this.#oivCoreSharedFacade.updateLibLanguage(language);
        this.#oivCoreSharedFacade.updateLinks(language);
        this.#tealiumTrackingFacade.trackChangeLanguage(language);
    }
}
